import * as React from "react";
import {
  FunctionComponent,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";
import { LoadStatus, StoreState, WidgetInstance } from "../types";
import LazyloadWrapper from "./LazyloadWrapper";
import * as deepEqual from "fast-deep-equal";
import { connect, ConnectedProps, MapStateToProps } from "react-redux";
import { injectScript } from "../actions/LoadStates";

interface Props {
  widgetInstance: WidgetInstance | undefined;
  settings: {};
  initWidget: () => void;
  enableLazyload: boolean;
  children: ReactNode;
}

interface StateProps {
  loadStatus: LoadStatus;
}

type ReduxProps = ConnectedProps<typeof connector>;

const bsWidgetScriptUrl = "https://widget.bookingsuedtirol.com/v2/bundle.js";

const HgvWidgetContainer: FunctionComponent<Props & ReduxProps> = ({
  widgetInstance,
  children,
  settings,
  initWidget,
  loadStatus,
  injectScript,
  enableLazyload,
}) => {
  const prevSettingsRef = useRef(settings);
  const [isLazyLoaded, setIsLazyloaded] = useState(false);

  useEffect(() => {
    (isLazyLoaded || !enableLazyload) && injectScript(bsWidgetScriptUrl);
  }, [isLazyLoaded, enableLazyload]);

  useEffect(() => {
    loadStatus === "loaded" && initWidget();
  }, [loadStatus]);

  // Remount if settings change
  useEffect(() => {
    if (loadStatus !== "loaded") return;

    const settingsAreEqual = deepEqual(settings, prevSettingsRef.current);
    prevSettingsRef.current = settings;
    if (settingsAreEqual) return;
    widgetInstance?.unmount();
    initWidget();
  }, [settings, widgetInstance, loadStatus]);

  return <LazyloadWrapper onLoad={setIsLazyloaded}>{children}</LazyloadWrapper>;
};

const mapStateToProps: MapStateToProps<StateProps, Props, StoreState> = ({
  loadStates,
}): StateProps => ({
  loadStatus: loadStates.scripts[bsWidgetScriptUrl] ?? "unloaded",
});

const mapDispatchToProps = {
  injectScript,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

export default connector(HgvWidgetContainer);
